import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import { UserProfileEntity } from "@/modules/users/entities/usersEntity"
import { HustleupSmallIcon } from "@images/svgIcons/hustleUpSmallIcon"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { Circles } from "@modules/ui/components/circles"

type ProfileResumeProps = {
  user: UserProfileEntity
} & React.ComponentProps<"div">

export function ProfileResume({ user, ...props }: ProfileResumeProps) {
  const mySpaceNavigate = useMySpaceNavigate()

  const completion = UserProfileEntity.getProfileCompletion(user)
  const isOnlyMissingAvatar = UserProfileEntity.isOnlyMissingAvatar(user)

  const onCompletionClick = () => {
    mySpaceNavigate(Path.MySpace.Profile)
  }

  return (
    <div
      className="relative flex flex-col items-center justify-center gap-4 overflow-hidden rounded-4xl bg-linear-90 from-primary-500 to-primary-600 px-4 py-6 max-[1180px]:hidden"
      {...props}
    >
      {/* Profile avatar */}
      <div className="flex max-w-full flex-row items-center justify-center gap-4">
        <ProfileAvatar
          profile={{ avatar: user.avatar ?? null, username: user.username }}
          className={cn("size-25", completion.pcRatio !== 1 ? "size-15" : "size-30")}
        />

        <div className="flex w-full flex-col items-start overflow-hidden">
          <Text fontStyle="HS" className="line-clamp-2 w-full">{`${user.firstname} ${user.lastname}`}</Text>
          <Text fontStyle="BM" className="w-full truncate">
            @{user.username}
          </Text>
        </div>
      </div>
      {/* Profile completion ratio */}
      {completion.pcRatio !== 1 &&
        (isOnlyMissingAvatar ? (
          <div className="flex cursor-pointer flex-row items-center justify-center gap-2" onClick={onCompletionClick}>
            <WarningHexagonalIcon color="var(--error-500)" size={24} />
            <Text fontStyle="LS">{word("profile.form.avatar_missing")}</Text>
          </div>
        ) : (
          <div className="flex cursor-pointer flex-col gap-2" onClick={onCompletionClick}>
            <Circles count={10} filled={completion.current} color="var(--secondary-700)" progressionstyle="line" />
            <Text fontStyle="LS" className="text-center">
              {word("profile.form.completion", { percentage: Math.ceil(completion.pcRatio * 100) })}
            </Text>
          </div>
        ))}

      <HustleupSmallIcon className="absolute top-0 right-[-33px] h-full w-auto opacity-80" />
    </div>
  )
}
